import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { 
  Activity, 
  Database, 
  Trash2, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  BarChart3,
  Settings,
  Server,
  Zap
} from 'lucide-react';
import { adminApiClient as api } from '../../services/api';

interface RedisHealthData {
  status: string;
  timestamp: string;
  redis_connection: {
    connected: boolean;
    url: string;
  };
  services: {
    [key: string]: {
      status: string;
      total_cached_keys?: number;
      [key: string]: any;
    };
  };
  health_score: number;
  overall_performance: string;
}

interface PerformanceMetrics {
  timestamp: string;
  cache_performance: {
    total_cached_keys: number;
    estimated_memory_usage: string;
  };
  recommendations: Array<{
    type: string;
    message: string;
    priority: string;
  }>;
}

interface LiveStats {
  timestamp: string;
  connection_status: string;
  active_sessions: number;
  online_agents: number;
  pubsub_channels: number;
  total_cached_keys: number;
}

const RedisMonitoring: React.FC = () => {
  const [healthData, setHealthData] = useState<RedisHealthData | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics | null>(null);
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'health' | 'performance' | 'live' | 'management'>('health');

  // Auto-refresh live stats every 10 seconds
  useEffect(() => {
    if (activeTab === 'live') {
      fetchLiveStats();
      const interval = setInterval(fetchLiveStats, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const fetchHealthData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/redis/health');
      setHealthData(response.data);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to fetch Redis health data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/redis/performance');
      setPerformanceData(response.data);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to fetch performance metrics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLiveStats = async () => {
    try {
      const response = await api.get('/redis/live-stats');
      setLiveStats(response.data);
    } catch (error: any) {
      console.error('Failed to fetch live stats:', error);
    }
  };

  const clearConversationCache = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await api.delete(`/redis/cache/conversation/${conversationId}`);
      alert('Conversation cache cleared successfully');
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to clear conversation cache');
    } finally {
      setIsLoading(false);
    }
  };

  const performCacheCleanup = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/redis/cache/cleanup');
      alert(`Cache cleanup completed: ${response.data.total_keys_removed} keys removed`);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to perform cache cleanup');
    } finally {
      setIsLoading(false);
    }
  };

  const getHealthColor = (status: string, score?: number) => {
    if (status === 'healthy' || status === 'connected') {
      if (score && score >= 90) return 'text-green-600';
      if (score && score >= 70) return 'text-yellow-600';
      return 'text-green-600';
    }
    return 'text-red-600';
  };

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Redis Monitoring</h1>
          <p className="text-muted-foreground">Monitor Redis performance, health, and cache management</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              if (activeTab === 'health') fetchHealthData();
              if (activeTab === 'performance') fetchPerformanceData();
              if (activeTab === 'live') fetchLiveStats();
            }}
            disabled={isLoading}
            variant="outline"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div>
            <h4 className="font-medium text-red-600">Error</h4>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'health', label: 'Health Status', icon: CheckCircle },
            { key: 'performance', label: 'Performance', icon: BarChart3 },
            { key: 'live', label: 'Live Stats', icon: Activity },
            { key: 'management', label: 'Cache Management', icon: Settings }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Health Status Tab */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Redis Health Status</h2>
            <Button onClick={fetchHealthData} disabled={isLoading}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Check Health
            </Button>
          </div>

          {healthData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Overall Health */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Server className="w-5 h-5 mr-2" />
                    Overall Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className={`text-lg font-semibold ${getHealthColor(healthData.status, healthData.health_score)}`}>
                      {healthData.status.toUpperCase()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Score: {healthData.health_score?.toFixed(1)}%
                    </div>
                    <div className={`text-sm ${getPerformanceColor(healthData.overall_performance)}`}>
                      Performance: {healthData.overall_performance}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Connection Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Database className="w-5 h-5 mr-2" />
                    Connection
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className={`text-lg font-semibold ${healthData.redis_connection.connected ? 'text-green-600' : 'text-red-600'}`}>
                      {healthData.redis_connection.connected ? 'Connected' : 'Disconnected'}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {healthData.redis_connection.url}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Services Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Zap className="w-5 h-5 mr-2" />
                    Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-lg font-semibold">
                      {healthData.healthy_services}/{healthData.total_services}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Services Online
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {healthData && (
            <Card>
              <CardHeader>
                <CardTitle>Service Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(healthData.services).map(([serviceName, serviceData]) => (
                    <div key={serviceName} className="p-4 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium">{serviceName.replace('_', ' ').toUpperCase()}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${getHealthColor(serviceData.status)}`}>
                          {serviceData.status}
                        </span>
                      </div>
                      {serviceData.total_cached_keys !== undefined && (
                        <div className="text-sm text-muted-foreground">
                          Cached Keys: {serviceData.total_cached_keys}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Performance Metrics</h2>
            <Button onClick={fetchPerformanceData} disabled={isLoading}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Get Metrics
            </Button>
          </div>

          {performanceData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Cache Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">
                        {performanceData.cache_performance.total_cached_keys}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Cached Keys</div>
                      <div className="text-sm text-muted-foreground">
                        Memory: {performanceData.cache_performance.estimated_memory_usage}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {performanceData.recommendations.map((rec, index) => (
                      <div key={index} className={`p-3 rounded-lg border-l-4 ${
                        rec.priority === 'high' ? 'border-red-500 bg-red-50' :
                        rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                        'border-blue-500 bg-blue-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{rec.type.replace('_', ' ').toUpperCase()}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {rec.priority}
                          </span>
                        </div>
                        <p className="text-sm text-foreground mt-1">{rec.message}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Live Stats Tab */}
      {activeTab === 'live' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Live Statistics</h2>
            <div className="text-sm text-muted-foreground">
              Auto-refreshing every 10 seconds
            </div>
          </div>

          {liveStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Connection Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${getHealthColor(liveStats.connection_status)}`}>
                    {liveStats.connection_status}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Active Sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {liveStats.active_sessions}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Online Agents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {liveStats.online_agents}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Cached Keys</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    {liveStats.total_cached_keys}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Cache Management Tab */}
      {activeTab === 'management' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Cache Management</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Clear Conversation Cache</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Clear cached data for a specific conversation. Enter the conversation ID:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Conversation ID"
                    className="flex-1 px-3 py-2 border border-border rounded-md"
                    id="conversationId"
                  />
                  <Button
                    onClick={() => {
                      const input = document.getElementById('conversationId') as HTMLInputElement;
                      if (input.value) {
                        clearConversationCache(input.value);
                      }
                    }}
                    disabled={isLoading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Cache Cleanup</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Perform comprehensive cleanup of expired sessions and offline agents.
                </p>
                <Button
                  onClick={performCacheCleanup}
                  disabled={isLoading}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Cleanup
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedisMonitoring;